import React, { useEffect, useState } from "react";
import { Card, Nav, Tab } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { StatisticsService } from "./service";
import { ErrorToast } from "../../shared/toasters/toasters";
import LatestTab from "./components/LatestTab";
import GraphsTab from "./components/GraphsTab";
import StatisticsTab from "./components/StatisticsTab";
import LocationMapTab from "./components/LocationMapTab";

export interface LatestCoach {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
}

export interface LatestCompany {
  id: number;
  name: string;
  email: string;
  createdAt: string;
}

export interface LatestJob {
  id: number;
  title: string;
  company?: { name: string };
  location?: { name: string };
  category?: { name: string };
  createdAt: string;
}

export interface LatestMatch {
  id: number;
  coach?: { firstName: string; lastName: string };
  job?: { title: string; company?: { name: string } };
  createdAt: string;
}

export interface LatestFeatured {
  id: number;
  title: string;
  company?: { name: string };
  location?: { name: string };
}

export interface LatestData {
  latestCoaches?: LatestCoach[];
  latestCompanies?: LatestCompany[];
  latestJobs?: LatestJob[];
  latestMatches?: LatestMatch[];
  latestFeatured?: LatestFeatured[];
  lastActiveCoaches?: LatestCoach[];
  lastActiveCompanies?: LatestCompany[];
}

export interface GraphData {
  categories: string[];
  series: { name: string; data: number[] }[];
}

export interface PeriodCount {
  week: number;
  month: number;
  year: number;
}

interface SubscriptionPeriod {
  total: number;
  byPackage: {
    packageId: number;
    packageName: string;
    packageType: string;
    count: number;
  }[];
}

export interface StatData {
  coaches?: PeriodCount;
  companies?: PeriodCount;
  jobs?: PeriodCount;
  matches?: PeriodCount;
  subscriptions?: {
    week: SubscriptionPeriod;
    month: SubscriptionPeriod;
    year: SubscriptionPeriod;
  };
}

const StatisticsList: React.FC = () => {
  const { t } = useTranslation();

  const [latestData, setLatestData] = useState<LatestData | null>(null);
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [statData, setStatData] = useState<StatData | null>(null);
  const [activeTab, setActiveTab] = useState("latest");

  useEffect(() => {
    StatisticsService.getLatest().then(setLatestData).catch(ErrorToast);
    StatisticsService.getGraphs().then(setGraphData).catch(ErrorToast);
    StatisticsService.getStatistics().then(setStatData).catch(ErrorToast);
  }, []);

  return (
    <div className="statistics-page">
      <Tab.Container
        activeKey={activeTab}
        onSelect={(k) => k && setActiveTab(k)}
      >
        <Card className="mb-4">
          <Card.Body className="py-2">
            <Nav variant="pills" className="gap-1">
              <Nav.Item>
                <Nav.Link eventKey="latest">
                  <i className="ph-duotone ph-clock-counter-clockwise me-2" />
                  {t("statistics.tabLatest")}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="graphs">
                  <i className="ph-duotone ph-chart-line me-2" />
                  {t("statistics.tabGraphs")}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="statistics">
                  <i className="ph-duotone ph-chart-pie-slice me-2" />
                  {t("statistics.tabStatistics")}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="location">
                  <i className="ph-duotone ph-map-pin me-2" />
                  {t("statistics.tabLocation")}
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Body>
        </Card>

        <Tab.Content>
          <Tab.Pane eventKey="latest">
            <LatestTab data={latestData} />
          </Tab.Pane>
          <Tab.Pane eventKey="graphs">
            <GraphsTab data={graphData} />
          </Tab.Pane>
          <Tab.Pane eventKey="statistics">
            <StatisticsTab data={statData} />
          </Tab.Pane>
          <Tab.Pane eventKey="location">
            <LocationMapTab />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default StatisticsList;
